/**
 * ENG-9567 — place an archived agent's transcripts onto THIS host and restore
 * its session pin, so a migrated agent resumes the conversation it was having.
 *
 * This is the read half of ENG-9566's manifest. The manifest records what the
 * source host shipped, the layout it shipped it under, and the pin that says
 * which transcript is the live one; this module consumes all three.
 *
 * FOUR RULES, each of which exists because breaking it fails silently.
 *
 * 1. PLACE BY THE DESTINATION'S OWN PATH, NEVER THE SOURCE'S (AC2). The
 *    manifest's keys embed the SOURCE's encoded project dir. Reusing it would
 *    write a codename-keyed source's files into a codename-keyed location on an
 *    id-keyed destination — a directory nothing reads. The destination resolves
 *    its own `sessionTranscriptDir(getProjectDir(codeName))` and the source's
 *    encoding is used only to take the basename off each key.
 *
 * 2. A FILE THAT CANNOT BE VERIFIED IS DROPPED, NOT LANDED (AC3). Downloads go
 *    to a temp path and are renamed in only after they parse. ENG-5397 removed
 *    `--resume` entirely after a poisoned transcript survived respawns and took
 *    an agent silent for hours; the rotation gate now caps that at two
 *    respawns, but a clean gap is still strictly better than a torn file. A
 *    half-restored history also produces an agent that confidently
 *    half-remembers, which costs more trust than honest amnesia.
 *
 * 3. THE PIN IS RE-DATED, AND ONLY WRITTEN WHEN IT CAN ACTUALLY RESUME. The
 *    spawner resumes only the session `daily-session.json` names FOR TODAY, and
 *    only when that transcript is on disk. Copying the source's pin verbatim
 *    restores a stale date that `getOrCreateDailySession` immediately rolls
 *    over, silently replacing it with a fresh UUID; pinning a session whose
 *    transcript did not land sends the spawner down `rotated-missing-transcript`
 *    for the same net result. Both are checked, and each is its own status.
 *
 * 4. A GUARANTEED NO-OP MUST NEVER REPORT SUCCESS (AC6). With
 *    `AGT_DISABLE_SESSION_RESUME` set, this host never `--resume`s anything, so
 *    a restore cannot deliver continuity no matter how many bytes it places.
 *    That is checked FIRST, before any download, and is its own status.
 */
import { basename, dirname, join } from 'node:path';

import type { DrainManifest } from './drain-transcript-flush.js';
import { isSessionResumeDisabled } from './persistent-session.js';

export type RestoreStatus =
  /** Every file placed (or already present) and the pin restored. */
  | 'restored'
  /** This host never resumes; a restore is a guaranteed no-op. NOT success. */
  | 'resume-disabled'
  /** No manifest at the source address — nothing describes what to restore. */
  | 'no-manifest'
  /** Files placed, but no pin: a destination cannot tell which one is live. */
  | 'no-pin'
  /**
   * A pin naming a session whose transcript is NOT on this host's disk. The
   * spawner's own `sessionFileExists` gate would take `rotated-missing-transcript`
   * and mint a fresh id, so writing this pin buys nothing and claiming
   * continuity would be a lie.
   */
  | 'pin-orphaned'
  /** The manifest's keys span more than one encoded dir — placement is unsafe. */
  | 'layout-mismatch'
  /** Objects are in a restore-before-read storage tier. */
  | 'glacier'
  /** Some files placed, some failed. */
  | 'partial'
  /** Had a manifest and files, placed none. */
  | 'failed';

export interface RestoreResult {
  status: RestoreStatus;
  /** Newly written this run. */
  placed: number;
  /** Already present and up to date — the idempotent path (AC5). */
  skipped: number;
  /** Downloaded but unverifiable, so deliberately not landed (AC3). */
  dropped: number;
  total: number;
  destDir: string | null;
  pinRestored: boolean;
  /** The session the source said was live, when it recorded one. */
  pinnedSessionId: string | null;
}

export interface RestoreDeps {
  /** THIS host's transcript dir for the agent. Never the manifest's. */
  resolveDestTranscriptDir: (codeName: string) => string;
  /** Fetch the manifest at the source address; null when absent. */
  readManifest: () => DrainManifest | null;
  /** Download one key to `tmpPath`. `storageClass` drives the glacier outcome. */
  download: (args: { key: string; tmpPath: string }) => Promise<{
    ok: boolean;
    detail: string;
    /** From the listing. Diagnostic only — see OFFLINE_OBJECT_ERROR_CODE. */
    storageClass?: string;
    /** The S3 error code, when the failure carried one. */
    errorCode?: string | null;
  }>;
  /** Does the file verify as a complete transcript? (AC3) */
  verify: (tmpPath: string) => boolean;
  /** Rename temp → final. Must be atomic on the same filesystem. */
  commit: (tmpPath: string, finalPath: string) => void;
  /** Discard a temp file that failed verification. */
  discard: (tmpPath: string) => void;
  /** Local mtime in ms, or null when absent — drives the AC5 skip. */
  localMtimeMs: (path: string) => number | null;
  /**
   * Does THIS host hold a transcript for `sessionId`? Must be the same
   * predicate the spawner uses, not an inference from the manifest's keys —
   * it is the spawner's answer that decides whether a resume happens.
   */
  sessionFileExists: (codeName: string, sessionId: string) => boolean;
  /**
   * Pin `sessionId` as the live conversation, dated to the DESTINATION's
   * today. Returns false when it could not be written.
   */
  restorePin: (codeName: string, sessionId: string) => boolean;
  /** Injected so tests need no env mutation. */
  resumeDisabled: () => boolean;
  log: (msg: string) => void;
}

/**
 * The S3 error code meaning "this object is in an archive tier; restore it
 * first". THIS, not the storage class, is the authoritative signal.
 *
 * The class was tried and is wrong in both directions. A RESTORED object keeps
 * its `GLACIER` class, so a download failing for an unrelated reason —
 * `ExpiredToken`, access denied, a transport error — was reported as "restore
 * the tier", sending the operator to fix something already fixed. And an
 * archived Intelligent-Tiering object reports `INTELLIGENT_TIERING`, so it was
 * missed entirely and surfaced as an ordinary failure.
 *
 * (`GLACIER_IR` illustrates the same point from the other side: despite the
 * name it serves millisecond GETs and never raises this code.)
 */
const OFFLINE_OBJECT_ERROR_CODE = 'InvalidObjectState';

/**
 * Storage classes that USUALLY mean an object needs restoring first.
 *
 * A HINT FOR A MESSAGE, never a decision. The decision is
 * `OFFLINE_OBJECT_ERROR_CODE` above, for the reasons given there. This set
 * survives only because the manifest lookup has no error code to work from: it
 * describes candidates it could not read, and naming a likely cause is better
 * than saying nothing. Being occasionally wrong in a sentence is cheap; being
 * wrong in a branch is what sent operators to restore an already-restored tier.
 *
 * `GLACIER_IR` is deliberately absent: it serves millisecond GETs despite the
 * name, and listing it once aborted restores of perfectly readable objects.
 */
const OFFLINE_STORAGE_CLASSES = new Set(['GLACIER', 'DEEP_ARCHIVE']);

/**
 * Is this storage class one a GET usually cannot read until the object is
 * restored? Exported so the manifest lookup asks the SAME question rather than
 * keeping its own copy of the set — a second copy is exactly how `GLACIER_IR`
 * would come back, having been removed from the set above.
 */
export function isOfflineStorageClass(storageClass: string | null | undefined): boolean {
  return !!storageClass && OFFLINE_STORAGE_CLASSES.has(storageClass.toUpperCase());
}

/**
 * The single encoded directory segment every key shares, or null when they do
 * not share one.
 *
 * Placement takes each key's BASENAME, so two keys under different encoded dirs
 * that happen to share a basename would silently overwrite one another — one
 * conversation clobbering another. Rather than guess which wins, refuse the
 * whole restore. A manifest written by `flushAgentTranscriptsOnDrain` always
 * describes exactly one agent's one transcript dir, so this is an
 * assumption-violated signal, not an expected branch.
 */
export function singleEncodedDir(keys: string[]): string | null {
  if (keys.length === 0) return null;
  const dirs = new Set(keys.map((k) => dirname(k)));
  if (dirs.size !== 1) return null;
  const names = keys.map((k) => basename(k));
  if (new Set(names).size !== names.length) return null;
  return [...dirs][0]!;
}

/** Discard that cannot itself escape the loop it is cleaning up after. */
function safeDiscard(deps: RestoreDeps, tmpPath: string): void {
  try {
    deps.discard(tmpPath);
  } catch {
    // A leftover `.restore-tmp` is inert: not a *.jsonl, so neither the spawner
    // nor a later drain flush will pick it up.
  }
}

export async function restoreAgentTranscripts(
  codeName: string,
  deps: RestoreDeps,
): Promise<RestoreResult> {
  const empty = { placed: 0, skipped: 0, dropped: 0, total: 0, pinRestored: false, pinnedSessionId: null };

  // RULE 4 — checked before anything is fetched. Downloading first and then
  // reporting this would burn a cross-account read to achieve nothing, and
  // (worse) leave files on disk that the next spawn ignores.
  if (deps.resumeDisabled()) {
    deps.log(
      `[restore] AGT_DISABLE_SESSION_RESUME is set on this host — '${codeName}' will spawn with a fresh ` +
        `session id regardless of what is restored, so this would be a guaranteed no-op. Refusing rather ` +
        `than reporting a success that delivers no continuity (ENG-9567).`,
    );
    return { status: 'resume-disabled', destDir: null, ...empty };
  }

  const manifest = deps.readManifest();
  if (!manifest) {
    deps.log(
      `[restore] NO MANIFEST for '${codeName}' at the source address — nothing describes which objects ` +
        `belong to this agent or which session is live. Not restoring (ENG-9567).`,
    );
    return { status: 'no-manifest', destDir: null, ...empty };
  }

  const destDir = deps.resolveDestTranscriptDir(codeName);
  const keys = manifest.keys ?? [];

  if (!singleEncodedDir(keys) && keys.length > 0) {
    deps.log(
      `[restore] LAYOUT MISMATCH for '${codeName}': the manifest's ${keys.length} key(s) do not share a ` +
        `single encoded directory, or repeat a basename. Placing by basename could overwrite one ` +
        `conversation with another, so nothing is placed (ENG-9567).`,
    );
    return { status: 'layout-mismatch', destDir, ...empty, total: keys.length };
  }

  let placed = 0;
  let skipped = 0;
  let dropped = 0;

  for (const key of keys) {
    const finalPath = join(destDir, basename(key)); // RULE 1
    const tmpPath = `${finalPath}.restore-tmp`;

    // AC5 — idempotent. A local file at least as new as the archived copy is
    // the authority: this host may have kept running and written turns the
    // archive predates, and clobbering those would DESTROY newer history to
    // restore older.
    const localMs = deps.localMtimeMs(finalPath);
    // An unparseable `writtenAt` yields NaN, and EVERY comparison against NaN is
    // false — so the skip silently stops firing and the restore overwrites a
    // local file it cannot prove is older. That is the one outcome this rule
    // exists to prevent: destroying newer history to reinstate older. With no
    // usable archive timestamp the local copy is authoritative.
    const archivedMs = Date.parse(manifest.writtenAt);
    if (localMs !== null && (!Number.isFinite(archivedMs) || localMs >= archivedMs)) {
      skipped += 1;
      continue;
    }

    // Every dep call in this loop is wrapped. `download` and `commit` touch the
    // filesystem and the network, so either can THROW rather than resolve
    // falsy — and an escaping rejection loses the whole structured verdict:
    // Commander's top-level catch sets exit 1 and the operator never learns how
    // many transcripts were already placed, or which one failed. A thrown error
    // is just another way for one key to fail, and is accounted as one.
    let res: { ok: boolean; detail: string; storageClass?: string; errorCode?: string | null };
    try {
      res = await deps.download({ key, tmpPath });
    } catch (err) {
      dropped += 1;
      safeDiscard(deps, tmpPath);
      deps.log(
        `[restore] download threw for '${codeName}' '${key}': ${(err as Error)?.message ?? 'unknown'} (ENG-9567)`,
      );
      continue;
    }
    // Gated on the S3 ERROR CODE, not the storage class. The class is wrong in
    // both directions: a RESTORED object keeps its `GLACIER` class, so an
    // unrelated failure (ExpiredToken, access denied, transport) read as
    // "restore the tier" and sent the operator to fix something already fixed;
    // and an archived Intelligent-Tiering object reports `INTELLIGENT_TIERING`,
    // so it was missed entirely. Still gated on `!res.ok`, since an object that
    // downloaded was by construction not blocked by its tier.
    if (!res.ok && res.errorCode === OFFLINE_OBJECT_ERROR_CODE) {
      safeDiscard(deps, tmpPath);
      deps.log(
        `[restore] ARCHIVE-TIERED object for '${codeName}': '${key}'${
          res.storageClass ? ` (${res.storageClass})` : ''
        } returned ${OFFLINE_OBJECT_ERROR_CODE} and must be restored to a readable tier before it ` +
          `can be fetched (ENG-9567).`,
      );
      return {
        status: 'glacier',
        destDir,
        placed,
        skipped,
        dropped,
        total: keys.length,
        pinRestored: false,
        pinnedSessionId: manifest.sessionPin?.currentSessionId ?? null,
      };
    }
    if (!res.ok) {
      dropped += 1;
      safeDiscard(deps, tmpPath);
      deps.log(`[restore] download failed for '${codeName}' '${key}': ${res.detail} (ENG-9567)`);
      continue;
    }

    // RULE 2 — verify BEFORE the rename, so a torn file never becomes visible
    // under a name the spawner would hand to --resume.
    // A verify that THROWS is a file that did not verify — same outcome,
    // reached a different way.
    let verified: boolean;
    try {
      verified = deps.verify(tmpPath);
    } catch {
      verified = false;
    }
    if (!verified) {
      dropped += 1;
      deps.discard(tmpPath);
      deps.log(
        `[restore] DROPPED '${key}' for '${codeName}' — downloaded but did not verify as a complete ` +
          `transcript. Leaving a clean gap rather than landing a torn file (ENG-5397/ENG-9567).`,
      );
      continue;
    }
    // RE-CHECKED, because production `commit` is a REPLACING `renameSync` and the
    // mtime that authorised it was read BEFORE the download. A running agent can
    // create or append to `finalPath` in that window, and the rename would then
    // overwrite turns newer than anything in the archive — the same data loss
    // the pre-check exists to prevent, reached through a race instead.
    //
    // This NARROWS the window to the gap between this stat and the rename; it
    // does not close it. Closing it needs a no-replace primitive or coordination
    // with the writing agent, neither of which exists here. A conflict counts as
    // SKIPPED: the local file won, which is the correct outcome, not a failure.
    const nowLocalMs = deps.localMtimeMs(finalPath);
    if (nowLocalMs !== null && (!Number.isFinite(archivedMs) || nowLocalMs >= archivedMs)) {
      skipped += 1;
      safeDiscard(deps, tmpPath);
      deps.log(
        `[restore] SKIPPED '${key}' for '${codeName}' — the local transcript changed while the ` +
          `archived copy was downloading and is now at least as new. Keeping the local one (ENG-9567).`,
      );
      continue;
    }

    try {
      deps.commit(tmpPath, finalPath);
    } catch (err) {
      dropped += 1;
      safeDiscard(deps, tmpPath);
      deps.log(
        `[restore] commit threw for '${codeName}' '${key}': ${(err as Error)?.message ?? 'unknown'} — ` +
          `the file did not land (ENG-9567)`,
      );
      continue;
    }
    placed += 1;
  }

  const pinnedSessionId = manifest.sessionPin?.currentSessionId ?? null;
  const base = { placed, skipped, dropped, total: keys.length, destDir, pinnedSessionId };

  // Nothing landed at all. Report THAT, rather than a pin verdict describing
  // the smaller of the two problems.
  if (dropped > 0 && placed + skipped === 0) {
    deps.log(
      `[restore] FAILED for '${codeName}' — all ${dropped} file(s) were dropped; nothing was placed (ENG-9567).`,
    );
    return { status: 'failed', ...base, pinRestored: false };
  }

  // ── The pin ──────────────────────────────────────────────────────────────
  // Files without a working pin are orphans. `resolveSessionSpawnDecision`
  // resumes ONLY the session `daily-session.json` names, and it applies two
  // gates that each fail SILENTLY — the spawn simply starts fresh and the
  // restored bytes are never read:
  //
  //   1. `getOrCreateDailySession` rolls over unless `current.date` is the
  //      destination's today, OVERWRITING the pin with a new UUID. So the pin
  //      is re-dated on write (`restorePin`) rather than copied verbatim from
  //      the source — a source pin still carrying yesterday's date restores
  //      cleanly and then evaporates on the very next spawn.
  //   2. `sessionFileExists` sends it down `rotated-missing-transcript` when
  //      the pinned transcript is not on this host's disk.
  //
  // Both are checked here so a restore cannot report continuity it will not
  // deliver.
  if (!manifest.sessionPin) {
    deps.log(
      `[restore] NO PIN for '${codeName}' — the source could not READ its daily-session pin, so which ` +
        `conversation was live is unrecoverable. ${placed + skipped} file(s) are on disk as history; the ` +
        `next spawn starts fresh (ENG-9567).`,
    );
    return { status: 'no-pin', ...base, pinRestored: false };
  }
  if (!pinnedSessionId) {
    deps.log(
      `[restore] NO PIN for '${codeName}' — the source's pin was ${
        manifest.sessionPin.present ? 'present but unparseable' : 'genuinely absent (a fresh agent)'
      }, so there is no live session to resume. ${placed + skipped} file(s) are on disk as history` +
        `${manifest.sessionPin.raw ? '; the raw pin bytes are in the manifest for manual recovery' : ''} (ENG-9567).`,
    );
    return { status: 'no-pin', ...base, pinRestored: false };
  }
  if (!deps.sessionFileExists(codeName, pinnedSessionId)) {
    deps.log(
      `[restore] PIN ORPHANED for '${codeName}' — the live session '${pinnedSessionId}' has no transcript on ` +
        `this host, so the spawner would rotate the pin away and start fresh. Not writing a pin that ` +
        `cannot resume (ENG-9567).`,
    );
    return { status: 'pin-orphaned', ...base, pinRestored: false };
  }

  // Written LAST, so it never names a transcript not yet on disk.
  const pinRestored = deps.restorePin(codeName, pinnedSessionId);
  if (!pinRestored) {
    deps.log(
      `[restore] NO PIN restored for '${codeName}' — ${placed} file(s) are on disk but the pin could not be ` +
        `written, so the next spawn will start fresh (ENG-9567).`,
    );
    return { status: 'no-pin', ...base, pinRestored: false };
  }

  // `complete: false` means the SOURCE could not ship everything. The pinned
  // conversation resumes, but the history behind it has holes, so this is
  // reported as partial — never as a clean restore.
  if (manifest.complete === false) {
    deps.log(
      `[restore] PARTIAL for '${codeName}' — resumable, but the source flush reported ${
        manifest.missing?.length ?? 0
      } file(s) it could not ship, so earlier history is incomplete (ENG-9566/ENG-9567).`,
    );
  }
  const status: RestoreStatus = dropped > 0 || manifest.complete === false ? 'partial' : 'restored';

  return { status, ...base, pinRestored };
}
